import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ApiClientError,
  DecorKindSchema,
  type AlternativeMode,
  type ApiClientErrorCode,
  type Decor,
  type DecorKind,
} from '@pomoshnik/shared';
import { apiClient } from '@/lib/apiClient';
import { DecorCardView, type DecorCardState } from './DecorCardView';
import { useDecorImageUrls } from './useDecorImageUrls';
import { decorFilterToQuery, readDecorFilterParams } from './decorFilterParams';

/**
 * Container Экрана 2.2.7 (Карточка декора). См. ADR-0005.
 *
 * Loads decor via `apiClient.decors.get(kind, globalId)`. Wires:
 *   - alternatives mood click → navigate `/decors/:kind/:globalId/alternatives?mode=...`
 *   - share button → `navigator.share` + clipboard fallback (Phase 7 #3)
 *   - back → catalog `/decors/:kind`
 *
 * P-001 verbatim: react-router-dom v7 уже декодирует path params, поэтому
 * `params.globalId` приходит сюда уже verbatim Global_ID. Передаём его в
 * apiClient как есть; apiClient encodeURIComponent-ит обратно при создании URL.
 */
export function DecorCard() {
  const navigate = useNavigate();
  const params = useParams<{ kind: string; globalId: string }>();
  const [searchParams] = useSearchParams();
  const kindParse = DecorKindSchema.safeParse(params.kind);
  const kind: DecorKind = kindParse.success ? kindParse.data : 'facade';
  const globalId = params.globalId ?? '';
  // ADR-0055: активный фильтр пришёл в URL карточки (из каталога/подбора) →
  // протягиваем его в URL альтернатив (persistent intent, R3).
  const filterQuery = decorFilterToQuery(readDecorFilterParams(searchParams));

  const [state, setState] = useState<DecorCardState>('loading');
  const [decor, setDecor] = useState<Decor | undefined>(undefined);
  const [error, setError] = useState<{ code: ApiClientErrorCode; message: string } | undefined>(
    undefined,
  );

  useEffect(() => {
    if (!globalId) {
      setState('not-found');
      return;
    }
    setState('loading');
    setError(undefined);
    let cancelled = false;
    apiClient.decors.get(kind, globalId).then(
      (d) => {
        if (cancelled) return;
        setDecor(d);
        setState('success');
      },
      (err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiClientError && err.code === 'NOT_FOUND') {
          setState('not-found');
          return;
        }
        // Не leak-аем String(err) в UI (security audit): non-ApiClientError → пустой message.
        const apiErr =
          err instanceof ApiClientError
            ? { code: err.code, message: err.message }
            : { code: 'INTERNAL_ERROR' as ApiClientErrorCode, message: '' };
        setError(apiErr);
        setState('error');
      },
    );
    return () => {
      cancelled = true;
    };
  }, [kind, globalId]);

  // Фото-2 (PR-DATA4): signed-URL для hero-фото (ключ = imageFilename декора).
  const heroKeys = useMemo(() => [decor?.imageFilename], [decor?.imageFilename]);
  const { urls: imageUrls } = useDecorImageUrls(heroKeys);
  const heroImageUrl = decor?.imageFilename ? (imageUrls[decor.imageFilename] ?? null) : null;

  function onAlternativeMode(mode: AlternativeMode) {
    const suffix = filterQuery ? `&${filterQuery}` : '';
    navigate(
      `/decors/${encodeURIComponent(kind)}/${encodeURIComponent(globalId)}/alternatives?mode=${mode}${suffix}`,
    );
  }

  /**
   * Фаза 6 (item 4): своё меню «Переслать клиенту» (WhatsApp/Telegram/MAX) в
   * Presenter заменило системный `navigator.share`. Здесь — только запасной
   * канал «Скопировать сообщение»: единый текст пересылки в буфер обмена
   * (clipboard), c alert-уведомлением (Phase 8 заменит на toast).
   */
  async function onCopyMessage(text: string) {
    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(text);
        alert('Сообщение скопировано');
        return;
      } catch {
        // clipboard недоступен (insecure context / permission) → fallback ниже
      }
    }
    alert(text);
  }

  function onBackToCatalog() {
    // PR-W1: список каталога переехал на `/decors/:kind/catalog` (`/decors/:kind`
    // теперь фильтр-экран). «Назад в каталог» ведёт на список, а не на фильтр.
    navigate(`/decors/${encodeURIComponent(kind)}/catalog`);
  }

  return (
    <DecorCardView
      state={state}
      decor={decor}
      error={error}
      heroImageUrl={heroImageUrl}
      onAlternativeMode={onAlternativeMode}
      onCopyMessage={onCopyMessage}
      onBackToCatalog={onBackToCatalog}
    />
  );
}
